import { useState } from "react";
import { ModalNew as Modal } from "components/Modal/ModalNew";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { ResourceForm } from "components/ResourceForm/ResourceForm";
import { addResource } from "actions/companyActions";
import { showToast } from "utils/helpers";
import { useCurrentCompanyId } from "hooks/auth";

interface AddNewKeyPersonnelModalProps {
	isOpen: boolean;
	onClose: () => void;
	onCreated: (id: number) => void;
}

const baseInitialValues = {
	company_id: "",
	first_name: "",
	last_name: "",
	email: "",
	position: "",
	experience: "",
	roles: [1],
	location: "",
	sector_ids: [],
	projects_completed: [],
	disciplines: [{ expertise: "", disciplines: [] }],
	resume: [],
	resume_files: [],
	resume_files_is_default: {},
	resume_files_id: {},
	resume_files_is_remove: {},
};

const projectsDataStructure = {
	name: "",
	role: "",
	sector_id: [],
	location: "",
	value: "",
};

export const AddNewKeyPersonnelModal: React.FC<AddNewKeyPersonnelModalProps> = ({
	isOpen,
	onClose,
	onCreated,
}) => {
	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const currentCompanyId = useCurrentCompanyId();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const initialValues = { ...baseInitialValues, company_id: currentCompanyId };

	const handleSubmit = async (
		values: Record<string, unknown>,
		{
			setErrors,
			setStatus,
		}: {
			setErrors: (errors: Record<string, string> | string) => void;
			setStatus: (status: string | null) => void;
		}
	) => {
		try {
			setIsSubmitting(true);
			const result = await (dispatch as { (values: any): { id; data, } })(
				addResource(values)
			);
			const newId = result?.data?.id ?? result?.id;
			queryClient.invalidateQueries({ queryKey: ["keyPersonnel"] });
			if (newId) {
				onCreated(newId);
			}
			onClose();
			showToast("Key personnel added successfully.", "Success", true);
		} catch (e: unknown) {
			if (e && typeof e === "object" && "errors" in e) {
				const errorObj = e as {
					errors?: { email?: string[] };
					message?: string;
				};
				const emailError = errorObj.errors?.email?.[0];
				setStatus(errorObj.message ?? null);
				setErrors(
					emailError ? { email: emailError } : (errorObj.message ?? "")
				);
				showToast(errorObj.message ?? "Error", "Error");
			} else {
				const msg = "Unexpected Error occurred";
				setStatus(msg);
				setErrors(msg);
				showToast(msg, "Error");
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!isOpen) {
		return null;
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			headerContent="Add New Key Personnel"
		>
			{/* @ts-ignore */}
			<ResourceForm
				initialValues={initialValues}
				isSubmitting={isSubmitting}
				handleSubmit={handleSubmit}
				projectsDataStructure={projectsDataStructure}
				hideDefaultButtons={false}
			/>
		</Modal>
	);
};
